import useBreakpoint from "./hooks/useBreakpoint";
import useDisplayMode from "./hooks/useDisplayMode";
import useDocumentLang from "./hooks/useDocumentLang";
import React, { useEffect } from "react";

import { ACCOUNTS, PERMISSIONS } from "./routeAccess";

import Login from "./components/Login";
import Layout from "./components/Layout";
import LegalLayout from "./components/LegalLayout";
import RequireAuth from "./components/RequireAuth";
import UserProfileGuard from "./components/UserProfileGuard";
import Unauthorized from "./components/Unauthorized";
import PersistentLogin from "./components/PersistentLogin";

import { Routes, Route } from "react-router-dom";
import Home from "./components/Home";
import useLabels from "./hooks/useLabels";
import { useDispatch, useSelector } from "react-redux";
import { setLocale } from "./context/configs/configsSlice";
import "react-datepicker/dist/react-datepicker.css";
import { selectUser, selectConfigs } from "./context/selectors";
import { getItem } from "./services/LocalStorage";
import { socket } from "./socket";
import AdditionalInfo from "./components/AdditionalInfo";
import CookieBar from "./components/CookieBar";
import Loader from "./components/Loader";
import BackendStatusBanner from "./components/BackendStatusBanner";

import loadable from "@loadable/component";
// Moved out of OperationsForm/ so it stays in the main CSS (see the header of the file); keep this import
// before OperationsDetail: it fixes the cascade order against OperationsDetail's styles.
import "./styles/shared/operations_form.scss";
// Pinned here to keep the main CSS order unchanged (OperationsDetail used to import it first).
import "./components/SecondaryBar/styles/index.scss";
// AuditLog's styles stay in the main CSS, at this position: the changelog reuses its
// audit_action_badge and the widget sits in the detail pages.
import "./components/AuditLog/styles/index.scss";
// OperationsDetail's styles stay in the main CSS, at this exact position: the components in
// OperationsDetail/Details (eager, imported by Generic and Home) need them on every page.
import "./components/OperationsDetail/styles/index.scss";
// Frequently-used routes — kept in the main bundle (no lazy chunk).
import Tanks from "./components/Tanks";
import { ErrorBoundary } from "./components/ErrorBoundary/ErrorBoundary";
import { Fallback } from "./components/ErrorBoundary/Fallback";
import WarehouseUpdateRouter from "./components/WarehouseUpdateRouter";
import VerifyEmail from "./components/VerifyEmail";
import ForgotPassword from "./components/PasswordRecovery/ForgotPassword";
import ResetPassword from "./components/PasswordRecovery/ResetPassword";

// Operations routes: code-split out of the main bundle. Names kept bare so the route JSX is unchanged.
// Modules shared with other lazy routes (e.g. LotForm importing from OperationsForm) carry their own
// SCSS via static imports, so the CSS travels with whichever chunk loads them.
const OperationsForm = loadable(() => import("./components/OperationsForm"), { fallback: <Loader/> });
const OperationsDetail = loadable(() => import("./components/OperationsDetail"), { fallback: <Loader/> });
const OperationsHub = loadable(() => import("./components/OperationsHub"), { fallback: <Loader/> });
const LazyProtocolApplications = loadable(() => import("./components/ProtocolApplications"), { fallback: <Loader/> });
const LazyOperationsHistory = loadable(() => import("./components/OperationsHistory"), { fallback: <Loader/> });
const LazyAuditLogPage = loadable(() => import("./components/AuditLog/AuditLogPage"), { fallback: <Loader/> });
const LazyEventAlertsPage = loadable(() => import("./components/EventAlerts/EventAlertsPage"), { fallback: <Loader/> });
const LazyErrorReportsPage = loadable(() => import("./components/ErrorReports/ErrorReportsPage"), { fallback: <Loader/> });
const LazyAnnouncementsPage = loadable(() => import("./components/AnnouncementsPage/AnnouncementsPage"), { fallback: <Loader/> });
const LazyChangelogPage = loadable(() => import("./components/ChangelogPage/ChangelogPage"), { fallback: <Loader/> });
const LazyChangelogAdminPage = loadable(() => import("./components/ChangelogAdminPage/ChangelogAdminPage"), { fallback: <Loader/> });
const LazySubstanceForm = loadable(() => import("./components/SubstanceForm"), { fallback: <Loader/> });
const LazySubstanceWarehouseForm = loadable(() => import("./components/SubstanceWarehouseForm"), { fallback: <Loader/> });
const LazyWarehouseItemForm = loadable(() => import("./components/WarehouseItemForm"), { fallback: <Loader/> });
const LazyWineMakingProtocolForm = loadable(() => import("./components/WineMakingProtocolForm"), { fallback: <Loader/> });
const LazyProtocolForm = loadable(() => import("./components/ProtocolForm"), { fallback: <Loader/> });
const LazyWineries = loadable(() => import("./components/Wineries"), { fallback: <Loader/> });
const LazyWineryForm = loadable(() => import("./components/WineryForm"), { fallback: <Loader/> });
const LazyWineryDetail = loadable(() => import("./components/WineryDetail"), { fallback: <Loader/> });
const LazyUserForm = loadable(() => import("./components/UserForm"), { fallback: <Loader/> });
const LazyUserDetail = loadable(() => import("./components/UserDetail"), { fallback: <Loader/> });
const LazyClientForm = loadable(() => import("./components/ClientForm"), { fallback: <Loader/> });
const LazyClientDetail = loadable(() => import("./components/ClientDetail"), { fallback: <Loader/> });
const LazyTankForm = loadable(() => import("./components/TankForm"), { fallback: <Loader/> });
const LazyLotForm = loadable(() => import("./components/LotForm"), { fallback: <Loader/> });
const LazySubstanceDetail = loadable(() => import("./components/SubstanceDetail"), { fallback: <Loader/> });
const LazySupplierForm = loadable(() => import("./components/SupplierForm"), { fallback: <Loader/> });
const LazySupplierDetail = loadable(() => import("./components/SupplierDetail"), { fallback: <Loader/> });
const LazyVineyardForm = loadable(() => import("./components/VineyardForm"), { fallback: <Loader/> });
const LazyVineyardDetail = loadable(() => import("./components/VineyardDetail"), { fallback: <Loader/> });
const LazyProtocolApplicationForm = loadable(() => import("./components/ProtocolApplicationForm"), { fallback: <Loader/> });
const LazyProtocolApplicationDetail = loadable(() => import("./components/ProtocolApplicationDetail"), { fallback: <Loader/> });
const LazyProtocolApplicationReport = loadable(() => import("./components/ProtocolApplicationReport"), { fallback: <Loader/> });
const LazyApplicationTemplateForm = loadable(() => import("./components/ApplicationTemplateForm"), { fallback: <Loader/> });
const LazyProtocolApplicationWizard = loadable(() => import("./components/ProtocolApplicationWizard"), { fallback: <Loader/> });
const LazyProtocolApplicationResumeWizard = loadable(() => import("./components/ProtocolApplicationWizard/ResumeWizard"), { fallback: <Loader/> });

// Heavy route-only components — code-split out of the main bundle.
// Names kept bare (no "Lazy" prefix) so the route JSX below is unchanged.
const TankDetail = loadable(() => import("./components/TankDetail"), { fallback: <Loader/> });
const Lots = loadable(() => import("./components/Lots"), { fallback: <Loader/> });
const LotDetail = loadable(() => import("./components/LotDetail"), { fallback: <Loader/> });
const Protocols = loadable(() => import("./components/Protocols"), { fallback: <Loader/> });
const ProtocolDetail = loadable(() => import("./components/ProtocolDetail"), { fallback: <Loader/> });
const WineMakingProtocolDetail = loadable(() => import("./components/WineMakingProtocolDetail"), { fallback: <Loader/> });
const Clients = loadable(() => import("./components/Clients"), { fallback: <Loader/> });
const Warehouse = loadable(() => import("./components/Warehouse"), { fallback: <Loader/> });
const Report = loadable(() => import("./components/Report"), { fallback: <Loader/> });
const TrackingWrapper = loadable(() => import("./components/Tracking/TrackingWrapper"), { fallback: <Loader/> });
const UserCustomzation = loadable(() => import("./components/UserCustomization"), { fallback: <Loader/> });
const ImpersonationBanner = loadable(() => import("./components/ImpersonationBanner"), { fallback: null });
const AnnouncementBanner = loadable(() => import("./components/AnnouncementBanner"), { fallback: null });

function App() {
  const { isNavDrawer: isMobile } = useBreakpoint();
  const sessionUser = useSelector(selectUser);
  useDisplayMode(sessionUser?.type);
  const { area, locale } = useSelector(selectConfigs);
  useDocumentLang(locale);

  const [_getLabel, _getArea, setLabels] = useLabels();
  const dispatch = useDispatch();

  // const [touchStart, setTouchStart] = useState(null)
  // const [touchEnd, setTouchEnd] = useState(null)

  // // the required distance between touchStart and touchEnd to be detected as a swipe
  // const minSwipeDistance = 50

  // const onTouchStart = (e) => {
  //   setTouchEnd(null) // otherwise the swipe is fired even with usual touch events
  //   setTouchStart(e.targetTouches[0].clientX)
  // }

  // const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX)

  // const onTouchEnd = () => {
  //   if (!touchStart || !touchEnd) return
  //   const distance = touchStart - touchEnd
  //   const isLeftSwipe = distance > minSwipeDistance
  //   const isRightSwipe = distance < -minSwipeDistance
  //   if (isRightSwipe)  setIsOpen(true);
  //   if (isLeftSwipe)  setIsOpen(false);

  //   if (isLeftSwipe || isRightSwipe) console.log('swipe', isLeftSwipe ? 'left' : 'right')
  //   // add your conditional logic here
  // }

  useEffect(() => {
    const localStorageLanguage = getItem("language") || getItem("i18Language") || "it_IT";
    dispatch(
      setLocale({ locale: localStorageLanguage?.split("_")?.[0] || "it" })
    );
    setLabels();
  }, []);

  useEffect(() => {
    setLabels();
  }, [area, locale]);

  return (
    <ErrorBoundary fallbackRender={Fallback}>
      <BackendStatusBanner />
      <ImpersonationBanner />
      <AnnouncementBanner />
      <Routes>
        <Route path="/">
          {/* public routes */}
          <Route element={<Layout />}>
            <Route path="/login" exact element={<Login />} />
            <Route path="/unauthorized" exact element={<Unauthorized />} />
            <Route path="/verify-email" exact element={<VerifyEmail />} />
            <Route path="/forgot-password" exact element={<ForgotPassword />} />
            <Route path="/reset-password" exact element={<ResetPassword />} />
            <Route path="/activate" exact element={<ResetPassword mode="activation" />} />
          </Route>
          {/* public legal pages: landing-style layout, no hero */}
          <Route element={<LegalLayout />}>
            <Route
              path="/privacy"
              exact
              element={<AdditionalInfo type="privacy" />}
            />
            <Route
              path="/cookie"
              exact
              element={<AdditionalInfo type="cookie" />}
            />
            <Route
              path="/set-cookie"
              exact
              element={<CookieBar isBar={false} />}
            />
          </Route>
          <Route
            element={<PersistentLogin isMobile={isMobile} socket={socket} />}
          >
            {/* Route groups: who opens them (account type) and, for the tenant pages, the permission of the
                user in the active winery (see routeAccess.js). The platform ADMIN has no bypass: it is only in
                the groups that list it. Pages of a tenant API need the resource of that API. */}

            {/* platform admin [ 1 ] */}
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.admin} />}>
              <Route path="/admin" element={<Home />} />
              <Route path="/clients" exact element={<Clients />} />
              <Route path="/clients/new" exact element={<LazyClientForm />} />
              <Route path="/clients/:id" exact element={<LazyClientDetail />} />
              <Route path="/clients/:id/update" exact element={<LazyClientForm />} />
              <Route path="/admin/error-reports" exact element={<LazyErrorReportsPage />} />
              <Route path="/admin/announcements" exact element={<LazyAnnouncementsPage />} />
              <Route path="/admin/changelog" exact element={<LazyChangelogAdminPage />} />
            </Route>

            {/* any logged account: the home (an ADMIN goes on to /clients) and the public changelog */}
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.any} />}>
              <Route path="/" element={<Home isMobile={isMobile} />} />
              <Route path="/changelog" exact element={<LazyChangelogPage />} />
            </Route>

            {/* client only (the BE routes are CLIENT-only) */}
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.client} />}>
              <Route path="/audit" exact element={<LazyAuditLogPage />} />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.client} permission={PERMISSIONS.usersWrite} />}>
              <Route path="/users/new" exact element={<LazyUserForm />} />
            </Route>
            {/* The first winery is created with no active winery (the BE allows it): no permission is asked then. */}
            <Route
              element={
                <RequireAuth
                  allowedRoles={ACCOUNTS.client}
                  permission={PERMISSIONS.wineriesWrite}
                  allowWithoutWinery
                />
              }
            >
              <Route path="/wineries/new" exact element={<LazyWineryForm />} />
            </Route>

            {/* persons and devices [ 2, 3, 5 ]: reads */}
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.winery} permission={PERMISSIONS.operationsRead} />}>
              <Route path="/programming" exact element={<OperationsHub isMobile={isMobile} />} />
              <Route
                path="/programming/history"
                exact
                element={<LazyOperationsHistory isMobile={isMobile} />}
              />
              <Route
                path="/programming/:type/:id"
                exact
                element={<OperationsDetail isMobile={isMobile} />}
              />
              <Route
                path="/programming/history/:type/:id"
                exact
                element={<OperationsDetail isMobile={isMobile} />}
              />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.winery} permission={PERMISSIONS.lotsRead} />}>
              <Route path="/lots" exact element={<Lots isMobile={isMobile} />} />
              <Route path="/lots/:id" exact element={<LotDetail />} />
              <Route
                path="/lots/:id/tracking"
                exact
                element={<TrackingWrapper type="lot" />}
              />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.winery} permission={PERMISSIONS.tanksRead} />}>
              <Route
                path="/tanks"
                exact
                element={<Tanks isMobile={isMobile} />}
              />
              <Route path="/tanks/:id" exact element={<TankDetail isMobile={isMobile} />} />
              <Route
                path="/tanks/:id/tracking"
                exact
                element={<TrackingWrapper  type="tank"/>}
              />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.winery} permission={PERMISSIONS.protocolsList} />}>
              <Route
                path="/protocols/:protocolType"
                exact
                element={<Protocols isMobile={isMobile} />}
              />
              <Route
                path="/protocols"
                exact
                element={<Protocols isMobile={isMobile} />}
              />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.winery} permission={PERMISSIONS.workingProtocolsRead} />}>
              <Route
                path="/protocols/winemaking/:type/:id"
                exact
                element={<WineMakingProtocolDetail />}
              />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.winery} permission={PERMISSIONS.protocolsRead} />}>
              <Route
                path="/protocols/:type/:id"
                exact
                element={<ProtocolDetail />}
              />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.winery} permission={PERMISSIONS.protocolApplicationsRead} />}>
              <Route path="/protocol-applications/:id/report" exact element={<LazyProtocolApplicationReport />} />
              <Route path="/protocol-applications/:id" exact element={<LazyProtocolApplicationDetail isMobile={isMobile} />} />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.winery} permission={PERMISSIONS.suppliersRead} />}>
              <Route path="/suppliers/:id" exact element={<LazySupplierDetail />} />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.winery} permission={PERMISSIONS.vineyardsRead} />}>
              <Route path="/vineyards/:id" exact element={<LazyVineyardDetail />} />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.winery} permission={PERMISSIONS.wineriesRead} />}>
              <Route path="/wineries" exact element={<LazyWineries />} />
              <Route path="/wineries/:id" exact element={<LazyWineryDetail />} />
            </Route>
            {/* users: the own profile always, the others by USERS (UserProfileGuard) */}
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.winery} />}>
              <Route element={<UserProfileGuard action="read" />}>
                <Route path="/users/:id" exact element={<LazyUserDetail />} />
              </Route>
              <Route element={<UserProfileGuard action="write" />}>
                <Route path="/users/:id/update" exact element={<LazyUserForm />} />
                <Route
                  path="/users/:id/customize"
                  exact
                  element={<UserCustomzation />}
                />
              </Route>
            </Route>

            {/* persons [ 2, 3 ]: pages whose BE routes exclude the tank monitor */}
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.people} permission={PERMISSIONS.alertsRead} />}>
              <Route path="/alerts" exact element={<LazyEventAlertsPage />} />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.people} permission={PERMISSIONS.reportsExport} />}>
              <Route path="/report" exact element={<Report isMobile={isMobile} />} />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.people} permission={PERMISSIONS.warehouseRead} />}>
              <Route path="/warehouse" exact element={<Warehouse isMobile={isMobile} />} />
              <Route path="/warehouse/:type" exact element={<Warehouse isMobile={isMobile} />} />
              <Route
                path="/warehouse/:type/:id"
                exact
                element={<LazySubstanceDetail />}
              />
            </Route>

            {/* persons [ 2, 3 ]: writes */}
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.people} permission={PERMISSIONS.operationsWrite} />}>
              <Route
                path="/programming/:type/new"
                exact
                element={<OperationsForm isMobile={isMobile} />}
              />
              <Route
                path="/programming/:type/:id/duplicate"
                exact
                element={<OperationsForm isDuplicate={true} isMobile={isMobile} />}
              />
              <Route
                path="/programming/:type/:id/update"
                exact
                element={<OperationsForm isMobile={isMobile} />}
              />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.people} permission={PERMISSIONS.lotsWrite} />}>
              <Route path="/lots/new" exact element={<LazyLotForm />} />
              <Route path="/lots/:id/update" exact element={<LazyLotForm />} />
              <Route
                path="/lots/:id/duplicate"
                exact
                element={<LazyLotForm isDuplicate={true} isMobile={isMobile} />}
              />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.people} permission={PERMISSIONS.tanksWrite} />}>
              <Route path="/tanks/new" exact element={<LazyTankForm />} />
              <Route path="/tanks/:id/update" exact element={<LazyTankForm />} />
              <Route path="/tanks/:id/duplicate" exact element={<LazyTankForm isDuplicate={true} />} />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.people} permission={PERMISSIONS.drugsWrite} />}>
              <Route
                path="/substances/:type/new"
                exact
                element={<LazySubstanceForm />}
              />
              <Route
                path={"/substances/:type/:id/update"}
                exact
                element={<LazySubstanceForm />}
              />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.people} permission={PERMISSIONS.warehouseWrite} />}>
              <Route path="/warehouse/:type/new" element={<LazyWarehouseItemForm />} />
              <Route path="/warehouse/:type/:id/rectify" element={<LazyWarehouseItemForm mode="rectify" />} />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.people} permission={PERMISSIONS.warehouseOrDrugsWrite} />}>
              <Route path="/warehouse/:type/:id/update" element={<WarehouseUpdateRouter />} />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.people} permission={PERMISSIONS.workingProtocolsWrite} />}>
              <Route
                path="/protocols/winemaking/:type/new"
                exact
                element={<LazyWineMakingProtocolForm />}
              />
              <Route
                path="/protocols/winemaking/:type/:id/update"
                exact
                element={<LazyWineMakingProtocolForm />}
              />
              <Route
                path="/protocols/winemaking/:type/:id/duplicate"
                exact
                element={<LazyWineMakingProtocolForm isDuplicate={true} isMobile={isMobile} />}
              />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.people} permission={PERMISSIONS.protocolsWrite} />}>
              <Route
                path="/protocols/:type/new"
                exact
                element={<LazyProtocolForm />}
              />
              <Route
                path="/protocols/:type/:id/update"
                exact
                element={<LazyProtocolForm />}
              />
              <Route
                path="/protocols/:type/:id/duplicate"
                exact
                element={<LazyProtocolForm isDuplicate={true} />}
              />
            </Route>
            {/* Application templates: /winemaking-protocols (WINEMAKING_PROTOCOLS) */}
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.people} permission={PERMISSIONS.winemakingProtocolsWrite} />}>
              <Route path="/protocols/app-templates/new" exact element={<LazyApplicationTemplateForm />} />
              <Route path="/protocols/app-templates/:id/update" exact element={<LazyApplicationTemplateForm />} />
              <Route
                path="/protocols/app-templates/:id/duplicate"
                exact
                element={<LazyApplicationTemplateForm isDuplicate={true} />}
              />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.people} permission={PERMISSIONS.protocolApplicationsWrite} />}>
              <Route path="/protocol-applications/new/wizard" exact element={<LazyProtocolApplicationWizard />} />
              <Route path="/protocol-applications/new" exact element={<LazyProtocolApplicationForm />} />
              <Route path="/protocol-applications/:id/configure" exact element={<LazyProtocolApplicationResumeWizard />} />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.people} permission={PERMISSIONS.suppliersWrite} />}>
              <Route path="/suppliers/new" exact element={<LazySupplierForm />} />
              <Route path="/suppliers/:id/update" exact element={<LazySupplierForm />} />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.people} permission={PERMISSIONS.vineyardsWrite} />}>
              <Route path="/vineyards/new" exact element={<LazyVineyardForm />} />
              <Route path="/vineyards/:id/update" exact element={<LazyVineyardForm />} />
            </Route>
            <Route element={<RequireAuth allowedRoles={ACCOUNTS.people} permission={PERMISSIONS.wineriesWrite} />}>
              <Route path="/wineries/:id/update" exact element={<LazyWineryForm />} />
            </Route>
          </Route>
        </Route>
        
        {/* TO DO add a custom 404 page instead of <Unauthorized /> */}
        <Route path="*" element={<Unauthorized />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;
